import { useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, ChevronRight, Pencil, Trash2, Plus,
  UploadCloud, X, ArrowUp, ArrowDown, ImagePlus, RotateCw, Mail, MessageCircle, FlaskConical, Bell,
} from "lucide-react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import { mediaUrl } from "@/lib/api";
import { EtapaIcon } from "@/components/rastreo/EtapaIcon";
import { TipoIcon } from "@/components/rastreo/TipoIcon";
import { formatFechaHora, iconoTipo, isoAInputOperacion, inputOperacionAIso } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { ErrorState } from "@/components/common/ErrorState";
import { VideoItem } from "@/components/rastreo/VideoItem";

const isoToLocalInput = isoAInputOperacion;

export default function UnidadDetalleAdmin() {
  const { id } = useParams();
  usePageTitle("Detalle de unidad");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmarAvanzar, setConfirmarAvanzar] = useState(false);
  const [dialogoEvento, setDialogoEvento] = useState(null); // { evento? }
  const [confirmarBorrarEvento, setConfirmarBorrarEvento] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-unidad", id],
    queryFn: () => adminApi.get(`/admin/unidades/${id}`).then((r) => r.data),
  });
  const { data: tipos = [] } = useQuery({
    queryKey: ["tipos"],
    queryFn: () => adminApi.get("/tipos").then((r) => r.data),
  });

  const avanzarMutation = useMutation({
    mutationFn: () => adminApi.post(`/admin/unidades/${id}/avanzar`),
    onSuccess: () => {
      toast.success(`Unidad avanzada a "${data.siguiente_etapa.nombre}"`);
      queryClient.invalidateQueries({ queryKey: ["admin-unidad", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-unidades"] });
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
    onSettled: () => setConfirmarAvanzar(false),
  });

  const borrarEventoMutation = useMutation({
    mutationFn: (evento) => adminApi.delete(`/admin/eventos/${evento.id}`),
    onSuccess: () => {
      toast.success("Evento eliminado");
      queryClient.invalidateQueries({ queryKey: ["admin-unidad", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-unidades"] });
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
    onSettled: () => setConfirmarBorrarEvento(null),
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-32">
        <Loader2 className="animate-spin text-[var(--tc-accent)]" size={26} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center py-32">
        <ErrorState testId="unidad-detalle-error" mensaje="No se pudo cargar esta unidad. Intentá de nuevo." onRetry={refetch} />
      </div>
    );
  }

  const { unidad, cliente, etapas, eventos, fotos, etapa_actual, siguiente_etapa } = data;
  const eventosPorEtapa = Object.fromEntries(eventos.map((e) => [e.etapa_id, e]));
  // Fix A (2026-09-01): el historial muestra las etapas activas (con o sin evento) MÁS los
  // eventos de etapas que fueron desactivadas después de registrarse (no se pierden).
  const etapasActivasIds = new Set(etapas.map((et) => et.id));
  const filasHistorial = [
    ...etapas.map((et) => ({ etapa: et, evento: eventosPorEtapa[et.id] })),
    ...eventos.filter((ev) => !etapasActivasIds.has(ev.etapa_id) && ev.etapa).map((ev) => ({ etapa: ev.etapa, evento: ev })),
  ].sort((a, b) => a.etapa.orden - b.etapa.orden);

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <Link to="/admin/unidades" className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:text-[var(--tc-accent)]">
        <ArrowLeft size={15} /> Volver a unidades
      </Link>

      <div className="flex items-start justify-between gap-4 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
            <TipoIcon icono={iconoTipo(tipos, unidad.tipo)} size={22} />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-[var(--tc-text)]">{unidad.marca} {unidad.modelo}</h1>
            <p className="mt-0.5 font-mono text-xs text-[var(--tc-text-secondary)]">{unidad.vin}</p>
            <p className="mt-0.5 text-xs text-[var(--tc-text-secondary)]">{cliente?.nombre} · {unidad.anio} · {unidad.color}</p>
          </div>
        </div>
        <button
          data-testid={ADMIN.detalleEditUnidadButton}
          onClick={() => navigate(`/admin/unidades/${id}/editar`)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)]"
        >
          <Pencil size={14} /> Editar
        </button>
      </div>

      {/* Avanzar etapa — acción principal */}
      <div className="mt-5 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">Etapa actual</p>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
          <span className="inline-flex items-center gap-2 rounded-full bg-[var(--tc-accent-soft)] px-3.5 py-1.5 text-sm font-semibold text-[var(--tc-accent)]">
            <EtapaIcon nombre={etapa_actual?.icono} size={15} /> {etapa_actual?.nombre || "Sin etapa"}
          </span>
          <div className="flex gap-2.5">
            <button
              data-testid={ADMIN.detalleManualEventButton}
              onClick={() => setDialogoEvento({})}
              className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)]"
            >
              <Plus size={15} /> Cargar evento manual
            </button>
            {siguiente_etapa ? (
              <button
                data-testid={ADMIN.detalleAdvanceButton}
                onClick={() => setConfirmarAvanzar(true)}
                className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
              >
                Avanzar a {siguiente_etapa.nombre} <ChevronRight size={16} />
              </button>
            ) : (
              <span className="rounded-[var(--tc-radius)] bg-[var(--tc-state-done)] px-4 py-2.5 text-sm font-semibold text-white">
                Unidad entregada
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Historial de eventos */}
      <div className="mt-5 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">Historial de eventos</p>
        <div className="mt-3 divide-y divide-[var(--tc-border)]">
          {filasHistorial.map(({ etapa: et, evento: ev }) => {
            return (
              <div key={et.id} data-testid={ADMIN.detalleEventRow} className="flex items-center justify-between gap-3 py-3">
                <div className="flex items-center gap-3">
                  <span className={`flex h-8 w-8 items-center justify-center rounded-[var(--tc-radius)] ${ev ? "bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]" : "bg-gray-50 text-[var(--tc-state-pending)]"}`}>
                    <EtapaIcon nombre={et.icono} size={15} />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-[var(--tc-text)]">{et.nombre}</p>
                    {ev ? (
                      <p className="text-xs text-[var(--tc-text-secondary)]">
                        {formatFechaHora(ev.fecha)} · {ev.editado_por || ev.creado_por || "—"}
                        {ev.nota && <> · {ev.nota}</>}
                      </p>
                    ) : (
                      <p className="text-xs text-[var(--tc-state-pending)]">Sin registrar</p>
                    )}
                  </div>
                </div>
                {ev && (
                  <div className="flex shrink-0 gap-1.5">
                    <button data-testid={ADMIN.detalleEventEditButton} onClick={() => setDialogoEvento({ evento: ev })} className="rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] hover:text-[var(--tc-accent)]">
                      <Pencil size={14} />
                    </button>
                    <button data-testid={ADMIN.detalleEventDeleteButton} onClick={() => setConfirmarBorrarEvento(ev)} className="rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-red-50 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Fotos */}
      <FotosSection unidadId={id} fotos={fotos} onChange={refetch} />

      {/* Videos enviados por el destinatario */}
      <VideosSection unidadId={id} vin={unidad.vin} />

      {/* Notificaciones */}
      <NotificacionesSection unidadId={id} />

      {/* Confirmar avanzar */}
      <AlertDialog open={confirmarAvanzar} onOpenChange={setConfirmarAvanzar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Avanzar a "{siguiente_etapa?.nombre}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Se registrará la fecha y hora actuales, y el cambio se verá de inmediato en el rastreo público.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.confirmDialogCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid={ADMIN.confirmDialogConfirm} onClick={() => avanzarMutation.mutate()}>Avanzar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar borrar evento */}
      <AlertDialog open={Boolean(confirmarBorrarEvento)} onOpenChange={(v) => !v && setConfirmarBorrarEvento(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este evento?</AlertDialogTitle>
            <AlertDialogDescription>
              La unidad podría retroceder a una etapa anterior si este era el evento más avanzado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.confirmDialogCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid={ADMIN.confirmDialogConfirm} onClick={() => borrarEventoMutation.mutate(confirmarBorrarEvento)}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {dialogoEvento && (
        <EventoDialog
          unidadId={id}
          etapas={etapas}
          evento={dialogoEvento.evento}
          onClose={() => setDialogoEvento(null)}
          onSaved={() => { setDialogoEvento(null); refetch(); }}
        />
      )}
    </div>
  );
}

function EventoDialog({ unidadId, etapas, evento, onClose, onSaved }) {
  const [etapaId, setEtapaId] = useState(evento?.etapa_id || etapas[0]?.id || "");
  const [fecha, setFecha] = useState(isoToLocalInput(evento?.fecha) || isoToLocalInput(new Date().toISOString()));
  const [nota, setNota] = useState(evento?.nota || "");
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    const body = { etapa_id: etapaId, fecha: inputOperacionAIso(fecha), nota: nota || null };
    try {
      if (evento) {
        await adminApi.put(`/admin/eventos/${evento.id}`, body);
      } else {
        await adminApi.post(`/admin/unidades/${unidadId}/eventos`, body);
      }
      toast.success("Evento guardado");
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{evento ? "Corregir evento" : "Cargar evento manual"}</DialogTitle>
          <DialogDescription>Registra la fecha en la que la unidad pasó por esta etapa.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Etapa</label>
            <select data-testid={ADMIN.eventDialogEtapa} value={etapaId} onChange={(e) => setEtapaId(e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]">
              {etapas.map((et) => <option key={et.id} value={et.id}>{et.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Fecha y hora (zona de operación)</label>
            <input data-testid={ADMIN.eventDialogFecha} required type="datetime-local" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Nota (opcional)</label>
            <textarea data-testid={ADMIN.eventDialogNota} value={nota} onChange={(e) => setNota(e.target.value)} rows={2} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" />
          </div>
          {error && <p className="rounded-[var(--tc-radius)] bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <button data-testid={ADMIN.eventDialogSubmit} type="submit" disabled={guardando} className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FotosSection({ unidadId, fotos, onChange }) {
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [arrastrando, setArrastrando] = useState(false);
  const [confirmarBorrarFoto, setConfirmarBorrarFoto] = useState(null);
  const [dialogoEditarFoto, setDialogoEditarFoto] = useState(null);
  const [leyendaNueva, setLeyendaNueva] = useState("");
  const [tomadaEnNueva, setTomadaEnNueva] = useState(() => isoToLocalInput(new Date().toISOString()));
  const inputRef = useRef(null);
  const lleno = fotos.length >= 5;

  const subir = async (archivo) => {
    if (fotos.length >= 5) {
      toast.error("Esta unidad ya tiene el máximo de 5 fotos. Eliminá una para subir otra.");
      return;
    }
    const fd = new FormData();
    fd.append("archivo", archivo);
    if (leyendaNueva.trim()) fd.append("leyenda", leyendaNueva.trim());
    if (tomadaEnNueva) fd.append("tomada_en", inputOperacionAIso(tomadaEnNueva));
    setSubiendo(true);
    setProgreso(0);
    try {
      await adminApi.post(`/admin/unidades/${unidadId}/fotos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgreso(Math.round((e.loaded / e.total) * 100)),
      });
      toast.success("Foto subida");
      setLeyendaNueva("");
      setTomadaEnNueva(isoToLocalInput(new Date().toISOString()));
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubiendo(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setArrastrando(false);
    const archivo = e.dataTransfer.files?.[0];
    if (archivo) subir(archivo);
  };

  const eliminar = async (foto) => {
    try {
      await adminApi.delete(`/admin/fotos/${foto.id}`);
      toast.success("Foto eliminada");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirmarBorrarFoto(null);
    }
  };

  const mover = async (index, direccion) => {
    const nuevoOrden = [...fotos];
    const destino = index + direccion;
    if (destino < 0 || destino >= nuevoOrden.length) return;
    [nuevoOrden[index], nuevoOrden[destino]] = [nuevoOrden[destino], nuevoOrden[index]];
    try {
      await adminApi.put(`/admin/unidades/${unidadId}/fotos/orden`, nuevoOrden.map((f) => f.id));
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-5 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">
        Fotos ({fotos.length}/5)
      </p>

      {!lleno && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-[var(--tc-text)]">Leyenda (opcional, para la próxima foto)</label>
            <input
              data-testid={ADMIN.photoLeyendaInput}
              value={leyendaNueva}
              onChange={(e) => setLeyendaNueva(e.target.value)}
              placeholder="ej: Frente"
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-[var(--tc-text)]">Fecha en que se tomó (zona de operación)</label>
            <input
              data-testid={ADMIN.photoTomadaEnInput}
              type="datetime-local"
              value={tomadaEnNueva}
              onChange={(e) => setTomadaEnNueva(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            />
          </div>
        </div>
      )}

      <div
        data-testid={ADMIN.photoDropzone}
        onDragOver={(e) => { e.preventDefault(); if (!lleno) setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={lleno ? undefined : onDrop}
        onClick={() => !lleno && !subiendo && inputRef.current?.click()}
        className={`mt-3 flex flex-col items-center justify-center gap-2 rounded-[var(--tc-radius)] border-2 border-dashed p-8 text-center transition-colors ${
          lleno ? "cursor-not-allowed border-[var(--tc-border)] bg-gray-50" :
          arrastrando ? "cursor-pointer border-[var(--tc-accent)] bg-[var(--tc-accent-soft)]" : "cursor-pointer border-[var(--tc-border)] hover:border-[var(--tc-accent)]"
        }`}
      >
        <input
          data-testid={ADMIN.photoFileInput}
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          disabled={lleno}
          onChange={(e) => e.target.files?.[0] && subir(e.target.files[0])}
        />
        {subiendo ? (
          <>
            <Loader2 className="animate-spin text-[var(--tc-accent)]" size={22} />
            <p data-testid={ADMIN.photoUploadProgress} className="text-sm text-[var(--tc-text-secondary)]">Subiendo... {progreso}%</p>
          </>
        ) : lleno ? (
          <>
            <ImagePlus size={22} className="text-[var(--tc-state-pending)]" />
            <p className="text-sm text-[var(--tc-text-secondary)]">Ya llegaste al máximo de 5 fotos. Eliminá una para subir otra.</p>
          </>
        ) : (
          <>
            <UploadCloud size={22} className="text-[var(--tc-accent)]" />
            <p className="text-sm text-[var(--tc-text-secondary)]">Arrastrá una foto acá o hacé clic para seleccionarla</p>
          </>
        )}
      </div>

      {fotos.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {fotos.map((f, i) => (
            <div key={f.id} data-testid={ADMIN.photoItem} className="group relative overflow-hidden rounded-[var(--tc-radius)] border border-[var(--tc-border)]">
              <img src={mediaUrl(f.url)} alt={f.leyenda || "Foto de la unidad"} className="h-32 w-full object-cover" />
              {f.leyenda && (
                <p className="truncate bg-white/95 px-2 py-1 text-[11px] text-[var(--tc-text-secondary)]">{f.leyenda}</p>
              )}
              <div className="absolute inset-0 flex items-start justify-between bg-black/10 p-1.5 opacity-100 pointer-events-auto transition-opacity [@media(hover:hover)]:bg-black/0 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:group-hover:bg-black/20 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-hover:pointer-events-auto">
                <div className="flex flex-col gap-1">
                  <button data-testid={ADMIN.photoMoveUpButton} onClick={() => mover(i, -1)} disabled={i === 0} className="rounded bg-white/90 p-1 text-[var(--tc-text)] disabled:opacity-40"><ArrowUp size={12} /></button>
                  <button data-testid={ADMIN.photoMoveDownButton} onClick={() => mover(i, 1)} disabled={i === fotos.length - 1} className="rounded bg-white/90 p-1 text-[var(--tc-text)] disabled:opacity-40"><ArrowDown size={12} /></button>
                </div>
                <div className="flex flex-col gap-1">
                  <button data-testid={ADMIN.photoEditButton} onClick={() => setDialogoEditarFoto(f)} className="rounded bg-white/90 p-1 text-[var(--tc-text)]"><Pencil size={12} /></button>
                  <button data-testid={ADMIN.photoDeleteButton} onClick={() => setConfirmarBorrarFoto(f)} className="rounded bg-white/90 p-1 text-red-600"><X size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={Boolean(confirmarBorrarFoto)} onOpenChange={(v) => !v && setConfirmarBorrarFoto(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta foto?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará de la galería pública también. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.confirmDialogCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid={ADMIN.confirmDialogConfirm} onClick={() => eliminar(confirmarBorrarFoto)}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {dialogoEditarFoto && (
        <FotoEditDialog
          foto={dialogoEditarFoto}
          onClose={() => setDialogoEditarFoto(null)}
          onSaved={() => { setDialogoEditarFoto(null); onChange(); }}
        />
      )}
    </div>
  );
}

function FotoEditDialog({ foto, onClose, onSaved }) {
  const [leyenda, setLeyenda] = useState(foto.leyenda || "");
  const [tomadaEn, setTomadaEn] = useState(isoToLocalInput(foto.tomada_en));
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      await adminApi.put(`/admin/fotos/${foto.id}`, {
        leyenda: leyenda.trim() || null,
        tomada_en: tomadaEn ? inputOperacionAIso(tomadaEn) : null,
      });
      toast.success("Foto actualizada");
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar foto</DialogTitle>
          <DialogDescription>Leyenda y fecha en que se tomó (no la de subida).</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <img src={mediaUrl(foto.url)} alt={leyenda || "Foto de la unidad"} className="h-36 w-full rounded-[var(--tc-radius)] object-cover" />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Leyenda</label>
            <input
              data-testid={ADMIN.photoEditLeyenda}
              value={leyenda}
              onChange={(e) => setLeyenda(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Fecha en que se tomó (zona de operación)</label>
            <input
              data-testid={ADMIN.photoEditTomadaEn}
              type="datetime-local"
              value={tomadaEn}
              onChange={(e) => setTomadaEn(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            />
          </div>
          {error && <p className="rounded-[var(--tc-radius)] bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <button data-testid={ADMIN.photoEditSubmit} type="submit" disabled={guardando} className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ESTADO_ESTILOS_NOTIF = {
  enviada: "bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]",
  pendiente: "bg-amber-50 text-amber-600",
  fallida: "bg-red-50 text-red-600",
};
const CANAL_ICONO_NOTIF = { whatsapp: MessageCircle, email: Mail, mock: FlaskConical };

function VideosSection({ unidadId, vin }) {
  const queryClient = useQueryClient();
  const [enlaceNuevo, setEnlaceNuevo] = useState(null);
  const { data: videos = [] } = useQuery({
    queryKey: ["admin-videos", unidadId],
    queryFn: () => adminApi.get(`/admin/unidades/${unidadId}/videos`).then((r) => r.data),
  });
  const { data: enlaces = [] } = useQuery({
    queryKey: ["admin-enlaces-video", unidadId],
    queryFn: () => adminApi.get(`/admin/unidades/${unidadId}/enlaces-video`).then((r) => r.data),
  });
  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-videos", unidadId] });
    queryClient.invalidateQueries({ queryKey: ["admin-enlaces-video", unidadId] });
  };

  const crear = async () => {
    try {
      const { data } = await adminApi.post(`/admin/unidades/${unidadId}/enlaces-video`, {});
      setEnlaceNuevo(`${window.location.origin}/unidad/${encodeURIComponent(vin)}?enlace=${data.token}`);
      refrescar();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(enlaceNuevo);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar: selecciona el texto y cópialo a mano");
    }
  };
  const revocar = async (enlaceId) => {
    try {
      await adminApi.delete(`/admin/enlaces-video/${enlaceId}`);
      toast.success("Enlace desactivado");
      refrescar();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const quitar = async (videoId) => {
    try {
      await adminApi.delete(`/admin/videos/${videoId}`);
      toast.success("Video quitado de la unidad");
      refrescar();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section data-testid={ADMIN.videosSection} className="mt-6 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">Videos del destinatario ({videos.length}/3)</h2>
        <button data-testid={ADMIN.videoLinkCreate} onClick={crear} className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm font-medium hover:border-[var(--tc-accent)]">
          <Plus size={14} /> Generar enlace para enviar video
        </button>
      </div>
      <p className="mt-2 text-sm text-[var(--tc-text-secondary)]">
        El enlace es privado y solo sirve para esta unidad. Vence a los 30 días y puedes desactivarlo cuando quieras.
      </p>

      {enlaceNuevo && (
        <div className="mt-3 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-[var(--tc-surface)] p-3">
          <p className="text-xs text-[var(--tc-text-secondary)]">Cópialo ahora: por seguridad no se vuelve a mostrar.</p>
          <div className="mt-2 flex gap-2">
            <input data-testid={ADMIN.videoLinkUrl} readOnly value={enlaceNuevo} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 rounded border border-[var(--tc-border)] bg-white px-2 py-1.5 font-mono text-xs" />
            <button data-testid={ADMIN.videoLinkCopy} onClick={copiar} className="rounded bg-[var(--tc-accent)] px-3 py-1.5 text-xs font-semibold text-white">Copiar</button>
          </div>
        </div>
      )}

      {enlaces.length > 0 && (
        <ul className="mt-3 divide-y divide-[var(--tc-border)] text-sm">
          {enlaces.map((e) => (
            <li key={e.id} data-testid={ADMIN.videoLinkRow} className="flex items-center justify-between gap-3 py-2">
              <span className="text-[var(--tc-text-secondary)]">
                Creado {formatFechaHora(e.creado_en)} · {e.vigente ? `vence ${formatFechaHora(e.expira_en)}` : e.revocado ? "desactivado" : "vencido"}
              </span>
              {e.vigente && (
                <button data-testid={ADMIN.videoLinkRevoke} onClick={() => revocar(e.id)} className="font-medium text-red-600 hover:underline">Desactivar</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {videos.length > 0 && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {videos.map((v) => (
            <VideoItem
              key={v.id}
              video={v}
              accion={<button data-testid={ADMIN.videoDelete} onClick={() => quitar(v.id)} className="font-medium text-red-600 hover:underline">Quitar</button>}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function NotificacionesSection({ unidadId }) {
  const [reintentando, setReintentando] = useState(null);

  const { data: notifs, isError, refetch } = useQuery({
    queryKey: ["admin-notificaciones-unidad", unidadId],
    queryFn: () =>
      adminApi
        .get("/admin/notificaciones", { params: { unidad_id: unidadId, page_size: 25 } })
        .then((r) => r.data.resultados),
  });

  const reintentar = async (id) => {
    setReintentando(id);
    try {
      await adminApi.post(`/admin/notificaciones/${id}/reintentar`);
      toast.success("Reintento en curso");
      refetch();
      setTimeout(refetch, 3000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReintentando(null);
    }
  };

  return (
    <div className="mt-5 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">Notificaciones enviadas</p>
      {isError ? (
        <ErrorState testId="detalle-notificaciones-error" mensaje="No se pudo cargar el historial de notificaciones." onRetry={refetch} />
      ) : !notifs ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-[var(--tc-accent)]" size={18} /></div>
      ) : notifs.length === 0 ? (
        <p className="mt-3 flex items-center gap-2 text-sm text-[var(--tc-text-secondary)]"><Bell size={15} /> Todavía no se generaron notificaciones para esta unidad.</p>
      ) : (
        <div className="mt-3 divide-y divide-[var(--tc-border)]">
          {notifs.map((n) => {
            const Icono = CANAL_ICONO_NOTIF[n.canal] || Bell;
            return (
              <div key={n.id} data-testid={ADMIN.detalleNotificacionRow} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <p className="flex items-center gap-1.5 text-sm font-medium text-[var(--tc-text)]"><Icono size={14} /> {n.etapa?.nombre} · {n.canal}</p>
                  <p className="mt-0.5 font-mono text-xs text-[var(--tc-text-secondary)]">
                    {n.destinatario_nombre ? `${n.destinatario_nombre} · ${n.destinatario}` : n.destinatario}
                  </p>
                  <p className="mt-0.5 text-xs text-[var(--tc-text-secondary)]">{formatFechaHora(n.creado_en)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${ESTADO_ESTILOS_NOTIF[n.estado] || "bg-gray-100 text-gray-500"}`}>{n.estado}</span>
                  {n.estado === "fallida" && (
                    <button
                      data-testid={ADMIN.detalleNotificacionRetryButton}
                      onClick={() => reintentar(n.id)}
                      disabled={reintentando === n.id}
                      className="inline-flex items-center gap-1.5 rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-2.5 py-1.5 text-xs font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] disabled:opacity-50"
                    >
                      <RotateCw size={12} className={reintentando === n.id ? "animate-spin" : ""} /> Reintentar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
